import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN, Array } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import { reatomHoldersCharts } from '../../atoms/reatom-holders-charts'

import { TradeHoldersChartTooltip } from './trade-holders-chart-tooltip'
import { TradeHoldersChartsItem } from './trade-holders-charts-item'

import type { HoldersChartEntry } from '@root/entities/holders-charts/holders-chart-entry'
import type { PairModel } from '@root/entities/pair'

type TradeHolderChartsProps = {
  pair: PairModel
}

function chartEntryToCoords(entry: HoldersChartEntry) {
  return {
    x: entry.date.getTime(),
    y: entry.value.toNumber(),
  }
}

export const TradeHoldersCharts = ({ pair }: TradeHolderChartsProps) => {
  const { t } = useTranslation()

  const { requestStateAtom } = useMemo(
    () =>
      reatomHoldersCharts({
        network: pair.network,
        pairAddress: pair.address,
      }),
    [pair.address, pair.network],
  )
  const [requestState] = useAtom(requestStateAtom)
  const [holdersCount] = useAtom(pair.holdersCount)
  const [top10holding] = useAtom(pair.top10holding)
  const [insiders] = useAtom(pair.insiders)
  const refreshChartsState = useAction(requestStateAtom.refresh)

  const charts = requestState.value
  const tooltipElement = useMemo(
    () =>
      charts ? (
        <TradeHoldersChartTooltip
          date={undefined}
          dateFormatted={undefined}
          data={charts}
        />
      ) : (
        <React.Fragment />
      ),
    [charts],
  )
  const top10Points = useMemo(
    () => charts?.top10HoldingChart.map(chartEntryToCoords) ?? [],
    [charts?.top10HoldingChart],
  )
  const holdersCountPoints = useMemo(
    () => charts?.holdersCountChart.map(chartEntryToCoords) ?? [],
    [charts?.holdersCountChart],
  )
  const insidersHoldingPoints = useMemo(
    () => charts?.insidersHoldingChart.map(chartEntryToCoords) ?? [],
    [charts?.insidersHoldingChart],
  )

  return AsyncStateN.matchWaiting(requestState, {
    Success: (value) => (
      <Root>
        <TradeHoldersChartsItem
          title={t('trade.holders-charts.top_10_holders')}
          type="percentage"
          count={
            <>
              <Numeric
                value={
                  Array.lastOrUndefined(value.top10HoldingChart)?.value ??
                  top10holding
                }
                preset="percents"
              />
              %
            </>
          }
          points={top10Points}
        />
        <TradeHoldersChartsItem
          title={t('trade.holders-charts.holders')}
          type="number"
          count={
            <Numeric
              value={
                Array.lastOrUndefined(value.holdersCountChart)?.value ??
                holdersCount
              }
              preset="currency-amount-short"
            />
          }
          points={holdersCountPoints}
        />
        <TradeHoldersChartsItem
          title={t('trading.audit.insider_holding')}
          type="percentage"
          count={
            <>
              <Numeric
                value={
                  Array.lastOrUndefined(value.insidersHoldingChart)?.value ??
                  insiders.holding
                }
                preset="percents"
              />
              %
            </>
          }
          points={insidersHoldingPoints}
          tooltipElement={tooltipElement}
        />
      </Root>
    ),
    Error: () => (
      <ErrorRoot>
        <InfoMessage
          intent="error"
          variant="dt"
          onRetry={() => refreshChartsState()}
          withSeparator
        />
      </ErrorRoot>
    ),
    Waiting: () => (
      <Root>
        <TradeHoldersChartsItem.Skeleton />
        <TradeHoldersChartsItem.Skeleton />
        <TradeHoldersChartsItem.Skeleton />
      </Root>
    ),
  })
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
  padding: 12px 20px;
  overflow-y: hidden;
  overflow-x: auto;

  background: var(--background-2);
`
const ErrorRoot = styled.div`
  display: grid;
  padding: 6px;
`
